import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, ArrowRight, Loader2 } from 'lucide-react';
import SEO from '../components/SEO';
import { useT } from '../i18n';

/* Branded exit interstitial: /go?u=<external-url>.
   Keeps Kitxens in the reader's mind for 4s before sending them to the article. */

export default function GoPage() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const [secs, setSecs] = useState(4);

  const target = useMemo(() => {
    const raw = new URLSearchParams(window.location.search).get('u') || '';
    try {
      const u = new URL(raw);
      return ['http:', 'https:'].includes(u.protocol) ? u : null;
    } catch { return null; }
  }, []);

  useEffect(() => {
    if (!target) return undefined;
    if (secs <= 0) {
      window.location.replace(target.href);
      return undefined;
    }
    const t = setTimeout(() => setSecs((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secs, target]);

  const t = isEs ? {
    taking: 'Te llevamos a', now: 'en', s: 's…', goNow: 'Ir ahora',
    remember: 'Recuerda: cada tendencia que leas, Kitxens te ayuda a aplicarla en tu restaurante.',
    scan: 'Escanea tu web gratis (30s)',
    partner: '¿Compartes contenido gastro? Gana 30% recurrente recomendando Kitxens →',
    invalid: 'Enlace no válido.', back: 'Volver a Kitxens',
  } : {
    taking: 'Taking you to', now: 'in', s: 's…', goNow: 'Go now',
    remember: 'Remember: every trend you read, Kitxens helps you apply it to your restaurant.',
    scan: 'Scan your website free (30s)',
    partner: 'Do you share food-industry content? Earn 30% recurring referring Kitxens →',
    invalid: 'Invalid link.', back: 'Back to Kitxens',
  };

  return (
    <div className="min-h-screen bg-[#0B0D12] flex items-center justify-center px-6" data-testid="go-page">
      <SEO path="/go" title="Kitxens" noindex />
      <div className="max-w-lg w-full text-center">
        <p className="text-2xl font-bold text-white">kit<span className="text-[#F77F11]">x</span>ens</p>
        <p className="mt-1 text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF9B4A]">Restaurant Operating Intelligence</p>
        {target ? (
          <>
            <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.04] p-6">
              <p className="flex items-center justify-center gap-2 text-[14.5px] text-neutral-300">
                <Loader2 className="w-4 h-4 animate-spin text-[#F77F11]" />
                {t.taking} <span className="font-bold text-white">{target.hostname.replace('www.', '')}</span> {t.now} <span className="font-bold text-[#FF9B4A] tabular-nums">{secs}{t.s}</span>
              </p>
              <a href={target.href} rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-[13.5px] font-bold text-[#F77F11] hover:underline underline-offset-4" data-testid="go-now-link">
                {t.goNow} <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
            <p className="mt-6 text-[13.5px] text-neutral-400 leading-relaxed">{t.remember}</p>
            <Link to="/index-ai#scan"
              className="mt-4 inline-flex items-center gap-2 bg-[#F77F11] hover:bg-[#d8651a] text-white h-11 px-6 rounded-full text-[14px] font-bold transition-colors" data-testid="go-scan-cta">
              {t.scan} <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-4 text-[12.5px]">
              <Link to="/affiliates" className="text-[#FF9B4A] font-semibold hover:underline underline-offset-4" data-testid="go-partner-cta">{t.partner}</Link>
            </p>
          </>
        ) : (
          <div className="mt-8">
            <p className="text-neutral-400">{t.invalid}</p>
            <Link to="/" className="mt-3 inline-block text-[#F77F11] font-bold hover:underline" data-testid="go-back-link">{t.back}</Link>
          </div>
        )}
      </div>
    </div>
  );
}
